import type { QueryClient } from '@tanstack/react-query';

import type { Crm } from 'domain/crm';
import type { DataCreditsStateDto } from 'domain/quota';
import { calculateRevealCost, decreaseQuota, getCrmExportPrice } from 'domain/quota/operations';
import type { ExportQuota } from 'domain/user';
import type { UserRepository } from 'features/user/repository';

import quotaApi from './api';
import quotaQueryKeys from './queryKeys';

type FactoryDeps = {
  userRepository: UserRepository;
};

export interface QuotaRepository {
  decreaseContactsQuota(count?: number): void;

  decreaseCrmExportsQuota(crm: Crm): void;

  notifyAdmin(): Promise<void>;
}

export const makeQuotaRepository = (queryClient: QueryClient, { userRepository }: FactoryDeps): QuotaRepository => {
  const invalidateDataCreditsQuery = async () => {
    try {
      return await queryClient.invalidateQueries({
        queryKey: quotaQueryKeys.dataCredits(),
      });
    } catch (message) {
      return console.error(message);
    }
  };

  return {
    decreaseContactsQuota: (count = 1): void => {
      const user = userRepository.getUser();

      if (!user || user.isImpersonate) {
        return;
      }

      if (userRepository.isDataCreditsEnabled) {
        queryClient.setQueryData<DataCreditsStateDto>(quotaQueryKeys.dataCredits(), (current) =>
          current
            ? {
                ...current,
                usageState: decreaseQuota(current.usageState, calculateRevealCost(count, current.dataPoints.contact)),
              }
            : current
        );

        void invalidateDataCreditsQuery();
      } else {
        userRepository.setUser({
          ...user,
          contactsQuota: { ...user.contactsQuota, ...decreaseQuota(user.contactsQuota, count) },
        });
      }
    },

    decreaseCrmExportsQuota: (crm: Crm): void => {
      const user = userRepository.getUser();

      if (!user || user?.isImpersonate) {
        return;
      }

      if (userRepository.isDataCreditsEnabled) {
        queryClient.setQueryData<DataCreditsStateDto>(quotaQueryKeys.dataCredits(), (current) =>
          current
            ? {
                ...current,
                usageState: decreaseQuota(current.usageState, getCrmExportPrice(current.dataPoints, crm)),
              }
            : current
        );

        void invalidateDataCreditsQuery();
      } else {
        queryClient.setQueryData<ExportQuota | undefined>(quotaQueryKeys.exportQuota(), (quotaQueryData) =>
          quotaQueryData ? { ...quotaQueryData, ...decreaseQuota(quotaQueryData) } : undefined
        );
      }
    },

    notifyAdmin: (): Promise<void> => quotaApi.notifyAdminForQuota(),
  };
};
